import type { VoidFunctionComponent } from 'react';

import { BinaryDependencyConfidence } from '../../../../shared/binaryDependencyConfidence';
import { icons, TooltipBadge } from '../../generic';
import type { BadgeColor } from '../../generic';

const ConfidenceColours: Record<BinaryDependencyConfidence, BadgeColor> = {
  [BinaryDependencyConfidence.High]: 'white',
  [BinaryDependencyConfidence.Medium]: 'light-yellow',
  [BinaryDependencyConfidence.Low]: 'light-red',
  [BinaryDependencyConfidence.Unknown]: 'light-blue',
};

const ConfidenceMessage: Record<BinaryDependencyConfidence, string> = {
  [BinaryDependencyConfidence.High]: 'We have high confidence this was included in your binary.',
  [BinaryDependencyConfidence.Medium]: 'We have moderate confidence this was included in your binary.',
  [BinaryDependencyConfidence.Low]: 'We have low confidence this was included in your binary.',
  [BinaryDependencyConfidence.Unknown]: 'Confidence value is Unknown',
};

type BinaryComponentBadgeProps = {
  confidence: BinaryDependencyConfidence | null,
  isLoading: boolean,
  isEnabled: boolean,
};

/**
 * Binary Component Confidence displays the confidence we have that this component is included
 * @param {BinaryComponentBadgeProps} props confidence, isLoading and isEnabled
 * @returns {React.ReactElement} Binary Component Badge
 */
export const BinaryComponentBadge: VoidFunctionComponent<BinaryComponentBadgeProps> = ({
  confidence,
  isLoading,
  isEnabled,
}) => {
  let confidenceColour: BadgeColor = 'lighter-gray';
  let tooltipMessage = 'Confidence value is missing';

  if (isLoading) {
    confidenceColour = 'white';
    tooltipMessage = 'Confidence loading...';
  }

  if (!isEnabled) {
    confidenceColour = 'lighter-gray';
    tooltipMessage = 'Confidence value unavailable - feature disabled';
  }

  if (confidence !== null) {
    confidenceColour = ConfidenceColours[confidence];
    tooltipMessage = ConfidenceMessage[confidence];
  }

  return (
    <TooltipBadge
      color={confidenceColour}
      id={`dependency-confidence-${confidence}`}
      icon={icons.boxesStacked}
      tooltip={tooltipMessage}
      fullWidth={false}
    >
      Binary Component
    </TooltipBadge>
  );
};

export default BinaryComponentBadge;
